import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import MetaTags from '../components/common/MetaTags'
import ResumeUploadCard from '../components/common/ResumeUploadCard'
import WheelLoader from '../components/common/WheelLoader'
import { useProfileContext } from '../components/layout/AuthenticatedLayout'
import { postsService } from '../services/postsService'
import type { PostListItem } from '../types'

export default function BlogPage() {
  const { profile } = useProfileContext()
  const [posts, setPosts] = useState<PostListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const data = await postsService.getAllPosts()
        setPosts(data)
      } catch {
        setError('Failed to load posts')
      } finally {
        setLoading(false)
      }
    }
    fetchPosts()
  }, [])

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this post? This cannot be undone.')) return

    setDeletingId(id)
    try {
      await postsService.deletePost(id)
      setPosts(posts.filter((p) => p.id !== id))
    } catch {
      setError('Failed to delete post')
    } finally {
      setDeletingId(null)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto">
        <MetaTags title="Blog | profile.work" description="Manage your blog posts" />

        <div className="mb-8">
          <h1
            className="text-2xl sm:text-3xl text-black mb-2"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Blog
          </h1>
          <p className="text-black/60">Create and manage your blog posts.</p>
        </div>

        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-6">
          <ResumeUploadCard
            title="No profile yet"
            subtitle="Upload your resume to create your professional profile first."
          />
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto">
        <MetaTags title="Blog | profile.work" description="Manage your blog posts" />
        <div className="text-center py-12">
          <div className="mx-auto mb-4">
            <WheelLoader width={80} />
          </div>
          <p className="text-black/60">Loading posts...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      <MetaTags title="Blog | profile.work" description="Manage your blog posts" />

      <div className="flex justify-between items-center mb-8">
        <div>
          <h1
            className="text-2xl sm:text-3xl text-black mb-2"
            style={{ fontFamily: "'Instrument Serif', serif" }}
          >
            Blog
          </h1>
          <p className="text-black/60">Create and manage your blog posts.</p>
        </div>
        <Link
          to="/blog/new"
          className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm"
        >
          New Post
        </Link>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-200 mb-6">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 p-12 text-center">
          <svg
            className="w-12 h-12 mx-auto text-black/20 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-black mb-2">No blog posts yet</h3>
          <p className="text-black/60 mb-6">Share your thoughts, ideas, and experiences.</p>
          <Link
            to="/blog/new"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create your first post
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div
              key={post.id}
              className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden hover:shadow-2xl transition-shadow"
            >
              <div className="flex">
                {post.cover_image_url && (
                  <div className="w-32 sm:w-40 flex-shrink-0">
                    <img src={post.cover_image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        post.status === 'published'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {post.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                    {post.published_at && (
                      <span className="text-xs text-black/50">{formatDate(post.published_at)}</span>
                    )}
                  </div>
                  <h2 className="text-lg font-semibold text-black mb-1 line-clamp-1">
                    {post.title}
                  </h2>
                  {post.excerpt && (
                    <p className="text-sm text-black/60 line-clamp-2">{post.excerpt}</p>
                  )}
                  <div className="flex items-center gap-3 mt-4">
                    <Link
                      to={`/blog/edit/${post.id}`}
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(post.id)}
                      disabled={deletingId === post.id}
                      className="text-sm text-red-500 hover:text-red-700 font-medium disabled:opacity-50"
                    >
                      {deletingId === post.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
